import { router, usePage } from "@inertiajs/react";
import {
  Box,
  Building,
  CircleHelp,
  ClipboardCheck,
  Handshake,
  Inbox,
  Keyboard,
  Link2,
  Moon,
  Plus,
  Sun,
  User,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { useHelp } from "@/components/help/help-provider";
import { RecordCreateModal } from "@/components/ui/record-create-modal";
import { type Appearance, useAppearance } from "@/hooks/use-appearance";
import { useShortcut } from "@/hooks/use-shortcut";
import { openShortcutsDialog } from "@/lib/shortcuts";
import {
  companies_path,
  deals_path,
  inbox_path,
  opportunities_path,
  people_path,
  search_path,
  tasks_path,
} from "@/routes";
import type { SharedData, User as UserType } from "@/types";

interface SearchResult {
  uid: string;
  name: string;
  subtitle: string | null;
  path: string;
}

interface SearchGroup {
  type: string;
  label: string;
  results: SearchResult[];
}

type RecordType = "company" | "deal" | "opportunity" | "person";

interface Action {
  id: string;
  title: string;
  icon: LucideIcon;
  run: () => void;
  /** Extra words that should match this action without appearing in its label. */
  keywords?: string;
  shortcut?: string;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

/** Record pages are /:account/:resource/:uid — three segments, last one a uid. */
const RECORD_PATH = /^\/[^/]+\/(opportunities|deals|companies|people)\/[^/]+$/;

// Global ⌘K / Ctrl-K palette. Mounted once in PersistentLayout; renders nothing
// outside the internal app (the portal shares no current_role). Record matches
// come from the /search endpoint; actions and quick-nav are local.
export function CommandPalette() {
  const props = usePage<SharedData>().props;
  const account = props.current_account as { uid: string } | undefined;
  const role = props.current_role as string | undefined;
  const currentUser = props.auth?.user as UserType | undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [createType, setCreateType] = useState<RecordType | null>(null);
  // ⌘J opens this same palette already scoped to the four creates, so the
  // shortcut still means "create a record" now that the sidebar's Create
  // dropdown is gone and this is the only door. Typing anything drops the
  // scope — a palette you have to escape from is a mode, not a shortcut.
  const [createOnly, setCreateOnly] = useState(false);
  // The uid the palette searched against; guards against a stale response
  // landing after the user kept typing.
  const latestQuery = useRef("");

  const accountUid = account?.uid;
  const { index: help, open: openHelp } = useHelp();
  const internal = Boolean(accountUid && role);
  const { appearance, updateAppearance } = useAppearance(
    currentUser?.appearance as Appearance | undefined,
  );

  const close = () => {
    setOpen(false);
    setQuery("");
    setCreateOnly(false);
  };

  // Guards live in the hook now. This binding previously had none at all, so
  // ⌘K inside a comment box or the brief editor opened the palette over what
  // you were writing.
  //
  // The modal guard is handled here rather than by the hook: the palette is
  // itself a dialog, so it must be able to close itself, but it must not open
  // on top of a create modal — which is what a plain toggle used to do.
  useShortcut(
    { key: "k", meta: true },
    () => {
      if (open) {
        close();
      } else if (!document.querySelector('[role="dialog"][data-state="open"]')) {
        setOpen(true);
      }
    },
    { enabled: internal, respectModals: false },
  );

  // The sidebar's search entry (and anything else that wants the palette)
  // asks for it by name instead of synthesising a ⌘K keydown, which only
  // worked by imitating the shortcut it happened to be bound to.
  useEffect(() => {
    const onOpen = () => {
      // Not on a page with no desk. The staff console has its own palette
      // answering this same event (INCO-521), and this one persists across
      // navigations: opened invisibly there, it would appear on the way back.
      if (!internal) return;
      if (open) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      setOpen(true);
    };
    window.addEventListener("command-palette:open", onOpen);
    return () => window.removeEventListener("command-palette:open", onOpen);
  }, [open, internal]);

  // ⌘J. preventDefault is doing real work — it is Downloads in Chrome and
  // Firefox — and the hook does it for us. No toggle: ⌘J on an open palette
  // would otherwise yank a half-typed search back to the create list.
  useShortcut(
    { key: "j", meta: true },
    () => {
      if (open) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      setCreateOnly(true);
      setOpen(true);
    },
    { enabled: internal, respectModals: false },
  );

  useEffect(() => {
    if (!accountUid) return;
    const term = query.trim();
    latestQuery.current = term;
    if (term.length < MIN_QUERY_LENGTH) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timeout = setTimeout(() => {
      fetch(search_path(accountUid, { q: term }), {
        headers: { Accept: "application/json" },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Search failed");
          return res.json();
        })
        .then((data: { query: string; groups: SearchGroup[] }) => {
          if (latestQuery.current !== data.query) return;
          setGroups(data.groups);
          setLoading(false);
        })
        .catch(() => {
          if (latestQuery.current !== term) return;
          setGroups([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [accountUid, query]);

  if (!internal || !accountUid) return null;

  const navigate = (path: string) => {
    close();
    router.visit(path);
  };

  const startCreate = (type: RecordType) => {
    close();
    setCreateType(type);
  };

  const quickNav: Action[] = [
    {
      id: "nav-inbox",
      title: "Inbox",
      icon: Inbox,
      keywords: "intake offers inbound review queue network",
      run: () => navigate(inbox_path(accountUid)),
    },
    { id: "nav-tasks", title: "Tasks", icon: ClipboardCheck, run: () => navigate(tasks_path(accountUid)) },
    {
      id: "nav-opportunities",
      title: "Opportunities",
      icon: Box,
      keywords: "mandates",
      run: () => navigate(opportunities_path(accountUid)),
    },
    { id: "nav-deals", title: "Deals", icon: Handshake, run: () => navigate(deals_path(accountUid)) },
    {
      id: "nav-companies",
      title: "Companies",
      icon: Building,
      keywords: "counterparties",
      run: () => navigate(companies_path(accountUid)),
    },
    { id: "nav-people", title: "People", icon: User, keywords: "contacts", run: () => navigate(people_path(accountUid)) },
  ];

  const onRecordPage = RECORD_PATH.test(window.location.pathname);

  const actions: Action[] = [
    {
      id: "new-opportunity",
      title: "New opportunity",
      icon: Plus,
      keywords: "create mandate",
      run: () => startCreate("opportunity"),
    },
    { id: "new-deal", title: "New deal", icon: Plus, keywords: "create", run: () => startCreate("deal") },
    {
      id: "new-company",
      title: "New company",
      icon: Plus,
      keywords: "create counterparty",
      run: () => startCreate("company"),
    },
    { id: "new-person", title: "New person", icon: Plus, keywords: "create contact", run: () => startCreate("person") },
    {
      id: "toggle-appearance",
      title: appearance === "dark" ? "Switch to Paper" : "Switch to Desk mode",
      icon: appearance === "dark" ? Sun : Moon,
      keywords: "theme dark light appearance",
      run: () => {
        updateAppearance(appearance === "dark" ? "light" : "dark");
        close();
      },
    },
    // Only offered where it means something. Brokers pass record links around
    // constantly, and the alternative is selecting the address bar by hand.
    ...(onRecordPage
      ? [
          {
            id: "copy-link",
            title: "Copy link to this record",
            icon: Link2,
            keywords: "share url",
            run: () => {
              navigator.clipboard
                .writeText(window.location.href)
                .then(() => toast.success("Link copied"))
                .catch(() => toast.error("Could not copy the link"));
              close();
            },
          } satisfies Action,
        ]
      : []),
    {
      id: "shortcuts",
      title: "Keyboard shortcuts",
      icon: Keyboard,
      keywords: "help cheatsheet keys",
      run: () => {
        close();
        openShortcutsDialog();
      },
      shortcut: "?",
    },
  ];

  const term = query.trim().toLowerCase();
  const searching = term.length >= MIN_QUERY_LENGTH;
  // cmdk's own filter is off because server results must not be re-filtered
  // client-side (the server already ranked them). Local items still need to
  // narrow as you type, so they are matched here.
  const matches = (action: Action) =>
    !term ||
    action.title.toLowerCase().includes(term) ||
    Boolean(action.keywords?.includes(term));

// Scoped to the creates when ⌘J opened this; everything else stands down so
// the list under "What do you want to create?" is only things that create.
const visibleActions = actions
  .filter(matches)
  .filter((action) => !createOnly || action.id.startsWith("new-"));
const visibleNav = createOnly ? [] : quickNav.filter(matches);

// Help articles, matched on title and summary, opened in the sheet rather
// than navigated to (INCO-386). Somebody who hits ⌘K mid-task to check one
// thing should not lose the screen they were on to read it.
//
// Capped at five: this is a group inside a palette, not a search results
// page, and "Open help centre" sits under it for anything longer.
const helpMatches = term && !createOnly
  ? help
      .filter(
        (article) =>
          article.title.toLowerCase().includes(term) ||
          article.summary.toLowerCase().includes(term),
      )
      .slice(0, 5)
  : [];

  return (
    <>
      <CommandDialog
        open={open}
        // Not bare `setOpen`: Esc and click-outside close the dialog without
        // going through `close()`, which left the query — and now the create
        // scope — sitting there for whoever opened it next.
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        title="Search"
        description="Search records, jump to a page, or run an action"
        shouldFilter={false}
      >
        <CommandInput
          placeholder={
            createOnly ? "What do you want to create?" : "Search records or type an action…"
          }
          value={query}
          onValueChange={(value) => {
            setQuery(value);
            if (value) setCreateOnly(false);
          }}
        />
        <CommandList>
          {visibleActions.length > 0 && (
            <CommandGroup heading="Actions">
              {visibleActions.map((action) => (
                <CommandItem key={action.id} value={action.id} onSelect={action.run}>
                  <action.icon />
                  {action.title}
                  {action.shortcut && <CommandShortcut>{action.shortcut}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
{internal && (helpMatches.length > 0 || Boolean(term)) && (
  <CommandGroup heading="Help">
    {helpMatches.map((article) => (
      <CommandItem
        key={article.slug}
        value={`help-${article.slug}`}
        onSelect={() => {
          close();
          openHelp(article.slug);
        }}
      >
        <CircleHelp />
        <div className="flex min-w-0 flex-col">
          <span className="truncate">{article.title}</span>
          <span className="text-muted-foreground truncate text-xs">
            {article.summary}
          </span>
        </div>
      </CommandItem>
    ))}
    <CommandItem
      value="help-centre"
      onSelect={() => {
        close();
        router.visit(`/${accountUid}/help`);
      }}
    >
      <CircleHelp />
      Open help centre
    </CommandItem>
  </CommandGroup>
)}
          {visibleNav.length > 0 && (
            <CommandGroup heading="Jump to">
              {visibleNav.map((item) => (
                <CommandItem key={item.id} value={item.id} onSelect={item.run}>
                  <item.icon />
                  {item.title}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {searching && !loading && groups.length === 0 && visibleActions.length === 0 && visibleNav.length === 0 && (
            <CommandEmpty>No matching records.</CommandEmpty>
          )}
          {searching &&
            groups.map((group) => (
              <CommandGroup key={group.type} heading={group.label}>
                {group.results.map((result) => (
                  <CommandItem
                    key={result.uid}
                    value={`${group.type}-${result.uid}`}
                    onSelect={() => navigate(result.path)}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{result.name}</span>
                      {result.subtitle && (
                        <span className="text-muted-foreground truncate text-xs">
                          {result.subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
        </CommandList>
      </CommandDialog>

      {createType && (
        <RecordCreateModal
          type={createType}
          open={Boolean(createType)}
          onOpenChange={(next) => !next && setCreateType(null)}
          onSuccess={(record) => {
            setCreateType(null);
            router.visit(`/${accountUid}/${PATH_SEGMENT[createType]}/${record.uid}`);
          }}
        />
      )}
    </>
  );
}

const PATH_SEGMENT: Record<RecordType, string> = {
  company: "companies",
  deal: "deals",
  opportunity: "opportunities",
  person: "people",
};
